import { app, type AppContext } from '../../game/context.js';
import type { FeatureManifest } from '../registry.js';
import { CitiesIcon } from './icon.js';
import { NET_CHECK, downloadableCities, downloadsOn, firstSessionOver, hasPacks, newCityCount, waitingUpdateCount } from './memo.js';

/**
 * LO-02 city packs (P2): the Cities screen lists the published packs (size, level range,
 * download with progress, pause / resume, update, delete, storage used). Downloads run in the
 * background over Wi-Fi by default and are checked against the manifest's sha256 and the 6 MB
 * budget. On every start the map hook registers the packs on this device (map, levels,
 * art; offline too); where downloads are on, it also resumes unfinished downloads and fetches
 * updates and new cities on Wi-Fi.
 */
const ENTRY = {
  order: 72,
  icon: CitiesIcon,
  label: 'feature.citypacks.rail',
  testId: 'rail-citypacks',
  open: 'cityPacks',
  // new cities, and updates that wait for the player to say yes (the network may be mobile data);
  // nothing during the first session (Flow A)
  badge: (a: AppContext) => (firstSessionOver(a) ? newCityCount(a) + waitingUpdateCount(a) || null : null),
};

/** The running app (null while there is none, e.g. unit tests reading the manifests). */
function running(): AppContext | null {
  try {
    return app();
  } catch {
    return null;
  }
}

export default {
  id: 'citypacks',
  // No manifest `flag` or `online` on purpose. The registry gates a feature's rail entry, its
  // journey teasers AND its map hook by the same two, but the packs already on this device must
  // register on every start (city tabs, levels) whatever the flag says and whether or not an API
  // is configured — offline, where /v1/config is not reachable and the bundled `cityPacks` default
  // (off) is in force, and in a build with no API base URL at all (`?api=off`), where `online`
  // used to take the map hook down with the rail entry and a downloaded city quietly dropped off
  // the map. So this feature gates its own two network-facing surfaces, the rail entry and the
  // journey teasers, and leaves the hook alone; the hook's network part (resume, list, updates and
  // new cities) is gated in index.tsx and new downloads in manager.ts.
  provides: ['cityPacks'],
  get entry() {
    const a = running();
    return !a || (a.services.api.enabled && downloadsOn(a)) ? ENTRY : undefined;
  },
  // first: downloaded cities join the map's journey before any card shows
  mapOrder: 5,
  // once per session: the packs on this device (when there are any), then, once downloads are
  // on (the flag may arrive after the first map visit), the network part
  mapDue: (a) => (downloadsOn(a) ? !a.session.checked.has(NET_CHECK) : !a.session.checked.has('citypacks') && hasPacks(a)),
  // published cities not downloaded yet: journey teasers ("Download in City Packs") that open the
  // Cities screen; none without an API, since nothing could be downloaded from there
  journey: (a) => (a.services.api.enabled ? downloadableCities(a).map((c) => ({ ...c, open: 'cityPacks' })) : []),
  load: () => import('./index.js'),
} satisfies FeatureManifest;
